
import React, { useCallback, useEffect, useState, useLayoutEffect, useMemo, useRef } from 'react';
import { collection, addDoc, setDoc, deleteDoc, updateDoc, onSnapshot, doc, getDoc, getDocs, arrayUnion, serverTimestamp, query, orderBy, where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import {  useNavigate, useLocation } from 'react-router-dom';
import { db, functions } from '../firebaseConfig';
import { getStorage } from 'firebase/storage';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { requestMicPermission } from '../utils/requestMicPermission';
import { setSpeakerRoute, resetSpeakerRoute } from '../plugins/audioRouter';
import { startIncomingCallAlert, stopIncomingCallAlert } from '../utils/incomingCallAlert';
import { saveToGallery, formatSaveMessage } from '../utils/saveToGallery';
import ZoomableImage from './ZoomableImage';
import { useCallSession } from '../context/CallSessionContext';
import { clearAllMissedCallNotifications } from '../utils/missedCallNotifications';
import './DirectChat.css';


// Legacy call-engine stubs kept only so older helper
// functions still type-check; real call engine now
// lives in CallSessionContext.
const callStartTimeRef = { current: null };
const pcRef = { current: null };
const answerPcRef = { current: null };
const localStreamRef = { current: null };
const remoteAudioRef = { current: null };
const remoteStreamRef = { current: null };
const iceUnsubRef = { current: null };
const callerCandidatesUnsubRef = { current: null };
const calleeCandidatesUnsubRef = { current: null };
const callerCandidatesProcessedRef = { current: new Set() };
const calleeCandidatesProcessedRef = { current: new Set() };
const pendingCalleeCandidatesRef = { current: [] };
const callTimeoutRef = { current: null };
const callPartnerIdRef = { current: null };

// No-op state setters for legacy helpers
const setIsCalling = () => {};
const setOutgoingCallId = () => {};
const setOutgoingCallTarget = () => {};
const setInCall = () => {};
const setIsMuted = () => {};
const setIsSpeakerOn = () => {};
const setIncomingCall = () => {};
const setIncomingCallId = () => {};


let hasLoggedIceServers = false;

const DirectChat = ({
  currentUser,
  otherUser,
  chatId,
  onBack,
  autoCallTarget = null,
  onAutoCallConsumed
}) => {
	
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  const navigate = useNavigate();


  const storage = getStorage(undefined, 'blue-wire-e382b.firebasestorage.app');

  const [messages, setMessages] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [disappearTime, setDisappearTime] = useState(0); // seconds
  const [menuOpen, setMenuOpen] = useState(false);
  const [editModeId, setEditModeId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [modalImage, setModalImage] = useState(null);
  const [confirmCallOpen, setConfirmCallOpen] = useState(false);
  const [confirmDeleteLogOpen, setConfirmDeleteLogOpen] = useState(false);
  const [pendingDeleteLogId, setPendingDeleteLogId] = useState(null);
  const [conversationDeleting, setConversationDeleting] = useState(false);
  const [conversationExists, setConversationExists] = useState(true);
  const redirectHandledRef = useRef(false);
  const deleteInitiatedRef = useRef(false);
  const isClosingConversationRef = useRef(false);
  const messageListenerUnsubRef = useRef(null);
  const conversationListenerUnsubRef = useRef(null);
  const callLogsListenerUnsubRef = useRef(null);

  const callSession = useCallSession();
  // Phase-2 invariant:
  // DirectChat is UI-only. CallSessionContext is the sole owner
  // of WebRTC setup, call lifecycle, and audio state.
  const callState = callSession?.state || {};
  const callActions = callSession?.actions || {};
  const callRefs = callSession?.refs || {};
  const terminationReasonRef = callRefs?.terminationReasonRef;

  const {
    incomingCall,
    incomingCallId,
    outgoingCallId,
    outgoingCallTarget,
    isCalling,
    inCall,
    isMuted,
    isSpeakerOn,
    callState: callLifecycleState,
    activeCallId,
    callStatus,
    callDuration,
  } = callState;

  const prevCallStateRef = useRef(callLifecycleState);
  const everConnectedRef = useRef(false);
  const lastCallIdRef = useRef(null);
  const lastRoleRef = useRef(null);
  const callStartAtRef = useRef(null);

  const autoAcceptCallIdRef = useRef(null);
  const autoAcceptInProgressRef = useRef(false);
  const autoAcceptSeededRef = useRef(null);
  const incomingPrefetchSeededRef = useRef(null);

  const canStartCall =
    !!callActions.startCall &&
    !!otherUser?.id &&
    !isCalling &&
    !inCall;



 
  const bottomRef = useRef(null);
  const editRef = useRef(null);
  const menuRef = useRef();
  const attachmentPickerRef = useRef(null);
  const messagesListRef = useRef(null);
  const prevMessagesRef = useRef([]);


	
const location = useLocation();
const fromProfile = location.state?.fromProfile || false;
  const autoAcceptCallId = location.state?.autoAcceptCallId || null;
  const incomingCallPrefetch = location.state?.incomingCallPrefetch;

  const getCallTimestampMs = useCallback((value) => {
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (value instanceof Date) return value.getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }, []);

  const clearCallRouteState = useCallback(() => {
    if (!isAndroidNative || !location.state) return;

    const nextState = { ...location.state };
    let changed = false;

    if ('autoAcceptCallId' in nextState) {
      delete nextState.autoAcceptCallId;
      changed = true;
    }

    if ('incomingCallPrefetch' in nextState) {
      delete nextState.incomingCallPrefetch;
      changed = true;
    }

    if (!changed) return;

    navigate(location.pathname, {
      replace: true,
      state: Object.keys(nextState).length ? nextState : null,
    }, (error) => {
      console.error("Direct call listener error:", error);
    });
  }, [isAndroidNative, location.pathname, location.state, navigate]);

  const isExpectedConversationTeardownError = useCallback((error) => (
    error?.code === 'permission-denied' || error?.code === 'not-found'
  ), []);

  const stopConversationSubscriptions = useCallback(() => {
    [
      messageListenerUnsubRef,
      conversationListenerUnsubRef,
      callLogsListenerUnsubRef,
    ].forEach((ref) => {
      if (typeof ref.current === 'function') {
        ref.current();
      }
      ref.current = null;
    });
  }, []);

  const handleConversationRemoved = useCallback((state = null) => {
    isClosingConversationRef.current = true;
    stopConversationSubscriptions();
    setConversationExists(false);
    setConversationDeleting(false);
    if (redirectHandledRef.current) return;
    redirectHandledRef.current = true;
    onBack(state);
  }, [onBack, stopConversationSubscriptions]);

  useEffect(() => {
    deleteInitiatedRef.current = false;
    isClosingConversationRef.current = false;
    redirectHandledRef.current = false;
    setConversationExists(true);
    setConversationDeleting(false);
  }, [chatId]);

  useEffect(() => {
    setMessages([]);
    setCallLogs([]);
  }, [chatId]);

 

const handleRemoveFile = (index) => {
  setSelectedFiles(prev => prev.filter((_, i) => i !== index));
};

  function getCallLogLabel(log, timeLabel) {
    const { status, direction, durationSeconds = 0 } = log;

    if (status === 'missed') {
      if (direction === 'outgoing') {
        return `Unanswered Call ${timeLabel || ''}`;
      }
      return `Missed Call ${timeLabel || ''}`;
    }

    if (status === 'declined') {
      return `Declined Call ${timeLabel || ''}`;
    }

    if (status === 'completed') {
      const seconds = durationSeconds || 0;
      const minutes = Math.floor(seconds / 60);
      const remaining = seconds % 60;
      const durationLabel = `(${minutes}m ${remaining}s)`;
      const arrow = direction === 'outgoing' ? '↗' : '↙';
      return `${arrow} ${timeLabel || ''} ${durationLabel}`;
    }

    const arrow = direction === 'outgoing' ? '↗' : '↙';
    return `${arrow} ${timeLabel || ''}`;
  }



  // Fetch messages
useEffect(() => {
    if (!chatId || !currentUser?.uid) return;

    const messagesRef = collection(db, 'conversations', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (isClosingConversationRef.current) {
        return;
      }

      const validMessages = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(msg => !msg.deleted);

      setMessages(validMessages);

      validMessages.forEach((msg) => {
        if (
          isClosingConversationRef.current ||
          msg.senderId === currentUser.uid ||
          msg.readBy?.includes(currentUser.uid)
        ) {
          return;
        }

        const msgRef = doc(db, 'conversations', chatId, 'messages', msg.id);
        void updateDoc(msgRef, {
          readBy: arrayUnion(currentUser.uid),
        }).catch((error) => {
          if (isExpectedConversationTeardownError(error)) {
            if (!isClosingConversationRef.current) {
              handleConversationRemoved(deleteInitiatedRef.current ? null : { convoDeleted: true });
            }
            return;
          }
          console.error('Read receipt update failed:', error);
        });
      });
    }, (error) => {
      if (isClosingConversationRef.current && isExpectedConversationTeardownError(error)) {
        return;
      }
      if (error?.code === 'permission-denied') {
        handleConversationRemoved(deleteInitiatedRef.current ? null : { convoDeleted: true });
        return;
      }
      console.error('Messages listener error:', error);
    });

    messageListenerUnsubRef.current = unsubscribe;

    return () => {
      if (messageListenerUnsubRef.current === unsubscribe) {
        messageListenerUnsubRef.current = null;
      }
      unsubscribe();
    };
  }, [chatId, currentUser?.uid, handleConversationRemoved, isExpectedConversationTeardownError]);

  // Watch conversation doc for deletion state
  useEffect(() => {
    if (!chatId) return;

    const convoRef = doc(db, 'conversations', chatId);
    const unsubscribe = onSnapshot(convoRef, (snap) => {
      if (isClosingConversationRef.current) {
        return;
      }

      if (!snap.exists()) {
        handleConversationRemoved(deleteInitiatedRef.current ? null : { convoDeleted: true });
        return;
      }

      const data = snap.data() || {};
      const isDeleting = data.deleting === true;
      if (isDeleting && !deleteInitiatedRef.current) {
        handleConversationRemoved({ convoDeleted: true });
        return;
      }

      setConversationDeleting(isDeleting);
      setConversationExists(true);
    }, (error) => {
      if (isClosingConversationRef.current && isExpectedConversationTeardownError(error)) {
        return;
      }
      if (error?.code === 'permission-denied') {
        handleConversationRemoved(deleteInitiatedRef.current ? null : { convoDeleted: true });
        return;
      }
      console.error('Conversation listener error:', error);
    });

    conversationListenerUnsubRef.current = unsubscribe;

    return () => {
      if (conversationListenerUnsubRef.current === unsubscribe) {
        conversationListenerUnsubRef.current = null;
      }
      unsubscribe();
    };
  }, [chatId, handleConversationRemoved, isExpectedConversationTeardownError]);

  // Subscribe to conversation-scoped call logs
  useEffect(() => {
    if (!chatId) {
      setCallLogs([]);
      return;
    }

    const logsRef = collection(db, 'conversations', chatId, 'callLogs');
    const q = query(logsRef, orderBy('startedAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (isClosingConversationRef.current) {
        return;
      }

      const entries = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setCallLogs(entries);
    }, (error) => {
      if (isClosingConversationRef.current && isExpectedConversationTeardownError(error)) {
        return;
      }
      if (error?.code === 'permission-denied') {
        handleConversationRemoved(deleteInitiatedRef.current ? null : { convoDeleted: true });
        return;
      }
      console.error('Call logs listener error:', error);
    });

    callLogsListenerUnsubRef.current = unsubscribe;

    return () => {
      if (callLogsListenerUnsubRef.current === unsubscribe) {
        callLogsListenerUnsubRef.current = null;
      }
      unsubscribe();
    };
  }, [chatId, handleConversationRemoved, isExpectedConversationTeardownError]);

  // Incoming ringing call Firestore listener is now centralized in IncomingCallManager.

  useEffect(() => {
    if (!callState.incomingCallId) {
      stopIncomingCallAlert();
      return;
    }

    if (Capacitor.isNativePlatform()) {
      return () => {
        stopIncomingCallAlert();
      };
    }

    startIncomingCallAlert();

    return () => {
      stopIncomingCallAlert();
    };
  }, [callState.incomingCallId]);

 


  useEffect(() => {
    if (inCall) {
      setSpeakerRoute(isSpeakerOn);
    }
}, [inCall, isSpeakerOn]);

  useEffect(() => {
    if (!chatId) return;
    clearAllMissedCallNotifications();
  }, [chatId]);

  // Conversation-scoped call logging: detect call end/missed and write a log
  useEffect(() => {
    const currentState = callLifecycleState;
    const prevState = prevCallStateRef.current;

    const currentActiveId = activeCallId;
    const currentOutgoingId = outgoingCallId;
    const currentIncomingId = incomingCallId;

    // Track active call id and our role for this call
    if (currentActiveId && !lastCallIdRef.current) {
      lastCallIdRef.current = currentActiveId;
      if (currentOutgoingId && currentOutgoingId === currentActiveId) {
        lastRoleRef.current = 'caller';
      } else if (currentIncomingId && currentIncomingId === currentActiveId) {
        lastRoleRef.current = 'callee';
      }
    }

    // Track whether this call ever reached connected state
    if (currentState === 'connected') {
      everConnectedRef.current = true;
      if (!callStartAtRef.current) {
        callStartAtRef.current = Date.now();
      }
    }

    const hasCallJustEnded =
      prevState &&
      prevState !== 'idle' &&
      currentState === 'idle' &&
      lastCallIdRef.current;

    if (
      hasCallJustEnded &&
      !isClosingConversationRef.current &&
      chatId &&
      currentUser?.uid &&
      otherUser?.id
    ) {
      const callIdForLog = lastCallIdRef.current;
      const role = lastRoleRef.current || 'caller';
      const everConnected = everConnectedRef.current;
      let durationSeconds = 0;

      if (everConnected && callStartAtRef.current) {
        const elapsedMs = Date.now() - callStartAtRef.current;
        durationSeconds = Math.max(
          0,
          Math.floor(elapsedMs / 1000)
        );
      }

      const logsRef = collection(db, 'conversations', chatId, 'callLogs');
      const logRef = doc(logsRef, callIdForLog);
      const now = serverTimestamp();

      const callerId =
        role === 'caller' ? currentUser.uid : otherUser.id;
      const calleeId =
        role === 'caller' ? otherUser.id : currentUser.uid;

      const terminalReason = terminationReasonRef?.current;
      let status = everConnected ? 'completed' : 'missed';
      if (!everConnected && role === 'callee' && terminalReason === 'declined') {
        status = 'declined';
      } else if (!everConnected && role === 'callee' && callStatus === 'declined') {
        status = 'declined';
      }
      const direction = role === 'caller' ? 'outgoing' : 'incoming';
      let callLogType = everConnected ? 'answered' : 'missed';
      if (!everConnected && status === 'declined') {
        callLogType = 'declined';
      }

      setDoc(
        logRef,
        {
          callId: callIdForLog,
          conversationId: chatId,
          callerId,
          calleeId,
          status,
          direction,
          durationSeconds,
          startedAt: now,
          endedAt: everConnected ? now : null,
          createdAt: now,
        },
        { merge: true }
      ).catch((err) => {
        if (!isExpectedConversationTeardownError(err)) {
          console.warn('Failed to write call log:', err);
        }
      });

      setDoc(
        doc(db, 'conversations', chatId),
        {
          lastCallLogAt: now,
          lastCallLogType: callLogType,
          lastCallLogBy: callerId,
        },
        { merge: true }
      ).catch((err) => {
        if (!isExpectedConversationTeardownError(err)) {
          console.warn('Failed to update call log metadata:', err);
        }
      });

      setDoc(
        doc(db, 'conversations', chatId, 'views', currentUser.uid),
        { lastViewed: serverTimestamp(), userId: currentUser.uid },
        { merge: true }
      ).catch((err) => {
        if (!isExpectedConversationTeardownError(err)) {
          console.warn('Failed to update conversation view state:', err);
        }
      });

      everConnectedRef.current = false;
      lastCallIdRef.current = null;
      lastRoleRef.current = null;
      callStartAtRef.current = null;
      if (terminationReasonRef) {
        terminationReasonRef.current = null;
      }
    }

    prevCallStateRef.current = currentState;
  }, [
    callLifecycleState,
    activeCallId,
    outgoingCallId,
    incomingCallId,
    chatId,
    currentUser?.uid,
    otherUser?.id,
  ]);




useEffect(() => {
    if (!autoCallTarget || autoCallTarget !== otherUser?.id) return;
    if (!currentUser?.uid) return;
    if (isCalling || inCall || outgoingCallId || incomingCallId) return;
    if (!callActions?.startCall) return;

    callActions.startCall(
      autoCallTarget,
      {
        name: otherUser?.firstInitialLastName || "Unknown",
        rank: otherUser?.rank || "",
        agency: otherUser?.agency || "Unknown Agency",
        avatarUrl: otherUser?.avatarUrl || "",
      },
      currentUser
    );
    onAutoCallConsumed?.();
  }, [
    autoCallTarget,
    otherUser?.id,
    currentUser?.uid,
    isCalling,
    inCall,
    outgoingCallId,
    incomingCallId,
    callActions,
    onAutoCallConsumed
  ]);

  useEffect(() => {
    if (!attachmentMenuOpen) return;

    const handlePointerDown = (event) => {
      if (attachmentPickerRef.current?.contains(event.target)) return;
      setAttachmentMenuOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [attachmentMenuOpen]);

  useEffect(() => {
    const prefetchStatus = String(incomingCallPrefetch?.status || '').toLowerCase();
    const prefetchCreatedAtMs =
      getCallTimestampMs(incomingCallPrefetch?.ringingAt) ||
      getCallTimestampMs(incomingCallPrefetch?.createdAt);
    const prefetchIsTerminal =
      ['declined', 'ended', 'failed', 'no_answer'].includes(prefetchStatus);
    const prefetchIsStale =
      !!prefetchCreatedAtMs && (Date.now() - prefetchCreatedAtMs) > 30000;
    const prefetchIsValid =
      !!incomingCallPrefetch?.id && !prefetchIsTerminal && !prefetchIsStale;

    if (
      autoAcceptCallId &&
      autoAcceptSeededRef.current !== autoAcceptCallId &&
      (!incomingCallPrefetch?.id || prefetchIsValid)
    ) {
      autoAcceptCallIdRef.current = autoAcceptCallId;
      autoAcceptSeededRef.current = autoAcceptCallId;
    }

    if (incomingCallPrefetch && incomingCallPrefetch.id) {
      const prefetchId = incomingCallPrefetch.id;
      if (prefetchIsValid && incomingPrefetchSeededRef.current !== prefetchId) {
        incomingPrefetchSeededRef.current = prefetchId;
        if (callActions?.setIncomingCallData) {
          callActions.setIncomingCallData(incomingCallPrefetch);
        }
      }

      if (!prefetchIsValid) {
        console.log('[DirectChat] Ignoring stale call route state', {
          callId: prefetchId,
          status: prefetchStatus || 'unknown',
          stale: prefetchIsStale,
        });
      }
    }

    if (isAndroidNative && (autoAcceptCallId || incomingCallPrefetch?.id)) {
      clearCallRouteState();
    }
  }, [
    autoAcceptCallId,
    incomingCallPrefetch,
    callActions,
    clearCallRouteState,
    getCallTimestampMs,
    isAndroidNative,
  ]);

  // Ensure we have the call doc when auto-accepting (cold start fallback)
  // No extra fetch; rely on incomingCallPrefetch passed from navigation

  useEffect(() => {
    if (
      !incomingCall ||
      !incomingCallId ||
      !autoAcceptCallIdRef.current ||
      incomingCallId !== autoAcceptCallIdRef.current ||
      autoAcceptInProgressRef.current
    ) {
      return;
    }

    autoAcceptInProgressRef.current = true;
    callActions?.acceptCall?.()
      .catch((err) => console.error("Auto-accept failed:", err))
      .finally(() => {
        autoAcceptInProgressRef.current = false;
        autoAcceptCallIdRef.current = null;
      });
  }, [incomingCall, incomingCallId, callActions]);


	

	useEffect(() => {
if (!Capacitor.isNativePlatform()) return;

  let willShow, willHide, didShow, didHide;

  (async () => {
    // "will" events (iOS) + "did" events (Android often fires these)
    willShow = await Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-open');
    });
    willHide = await Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-open');
    });

    // Fallbacks for Android
    didShow = await Keyboard.addListener('keyboardDidShow', () => {
      document.body.classList.add('keyboard-open');
    });
    didHide = await Keyboard.addListener('keyboardDidHide', () => {
      document.body.classList.remove('keyboard-open');
    });
  })();

  return () => {
    willShow?.remove?.();
    willHide?.remove?.();
    didShow?.remove?.();
    didHide?.remove?.();
  };
}, []);
	


	useEffect(() => {
  const handleClickOutside = (event) => {
    // Close edit mode if clicked outside
    if (
      editRef.current &&
      !editRef.current.contains(event.target) &&
      editModeId
    ) {
      handleSaveEdit(editModeId);
    }

    // Close the (...) menu if clicked outside
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target)
    ) {
      setMenuOpen(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [editModeId, editContent]);



// Remove the page scrolling when in the chat window
useEffect(() => {
  document.body.classList.add('dm-open');
  document.documentElement.classList.add('dm-open');
  return () => {
    document.body.classList.remove('dm-open');
    document.documentElement.classList.remove('dm-open');
  };
}, []);

  // 🔊 Unlock remote audio on mobile (required for Android/iOS autoplay)
useEffect(() => {
  const unlock = () => {
    const audioEl = callRefs.remoteAudioRef?.current;
    if (audioEl) {
      audioEl.play().catch(() => {});
    }
  };

  document.addEventListener("click", unlock, { once: true });
  document.addEventListener("touchstart", unlock, { once: true });

  return () => {
    document.removeEventListener("click", unlock);
    document.removeEventListener("touchstart", unlock);
  };
}, [callRefs.remoteAudioRef]);


useEffect(() => {
  return () => {
    const calleeUnsubRef = callRefs.calleeCandidatesUnsubRef;
    if (calleeUnsubRef?.current) {
      calleeUnsubRef.current();
      calleeUnsubRef.current = null;
    }
    const timeoutRef = callRefs.callTimeoutRef;
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
}, [callRefs.calleeCandidatesUnsubRef, callRefs.callTimeoutRef]);




	useEffect(() => {
  // Track timeouts so we can clean them up if needed
  const timeouts = [];

  messages.forEach((msg) => {
    if (msg.disappearAt) {
      const timeLeft = msg.disappearAt - Date.now();
      if (timeLeft > 0) {
        const timeoutId = setTimeout(() => {
          setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        }, timeLeft);
        timeouts.push(timeoutId);
      } else {
        // Already expired, remove immediately
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      }
    }
  });

  return () => {
    timeouts.forEach(clearTimeout);
  };
}, [messages]);



	useEffect(() => {
  const interval = setInterval(() => {
    setMessages(prev =>
      prev.filter(msg => !msg.disappearAt || Date.now() < msg.disappearAt)
    );
  }, 1000); // every 1 second

  return () => clearInterval(interval);
}, []);



	const scrollToBottom = (behavior = 'auto') => {
  const el = messagesListRef.current;
  if (!el) return;
  el.scrollTo({ top: el.scrollHeight, behavior });
};



	

useEffect(() => {
  if (!messages.length) return;

  const prevMessages = prevMessagesRef.current;
  const newMessageAdded =
    prevMessages.length && messages.length > prevMessages.length;

  // If this is the first load of messages → scroll immediately
  if (!prevMessages.length) {
    scrollToBottom('auto');
  }

  // If new message comes in and user is near the bottom → scroll smoothly
  if (newMessageAdded && isAtBottom) {
    scrollToBottom('smooth');
  }

  prevMessagesRef.current = messages;
}, [messages, isAtBottom]);





  // Send message
const handleSendMessage = async () => {
  if (isSendingMessage) return;
  if (!conversationExists) {
    alert('This conversation no longer exists.');
    return;
  }
  if (conversationDeleting) {
    alert('This conversation is being deleted.');
    return;
  }
  const trimmedMessage = messageInput.trim();
  if (!trimmedMessage && selectedFiles.length === 0) return;

  console.log('chatId for upload:', chatId);

  try {
    setIsSendingMessage(true);
    const fileUrls = [];

    // Upload files first (if any)
    for (const file of selectedFiles) {
      const sanitizedFileName = encodeURIComponent(file.name.replace(/\s+/g, '_'));
      const filePath = `uploads/${chatId}/${Date.now()}_${sanitizedFileName}`;
      const fileRef = ref(storage, filePath);
      await uploadBytes(fileRef, file, {
        contentType: file.type || 'application/octet-stream',
        customMetadata: { uploaderUid: currentUser.uid }
      });
      const url = await getDownloadURL(fileRef);
      fileUrls.push({ url, type: file.type.startsWith('image/') ? 'image' : 'file' });
    }

    // Create the message doc
    await addDoc(collection(db, 'conversations', chatId, 'messages'), {
      senderId: currentUser.uid,
      senderName: currentUser.firstInitialLastName || 'Unknown',
      createdAt: serverTimestamp(),
      content: messageInput,
      mediaAttachments: fileUrls.length > 0 ? fileUrls : [],
      expiresIn: disappearTime > 0 ? disappearTime : null,
      disappearAt: disappearTime > 0 ? Date.now() + disappearTime * 1000 : null,
      deleted: false,
      readBy: [currentUser.uid],
    });

    // Update the parent conversation for sorting/unread logic
    await updateDoc(doc(db, 'conversations', chatId), {
      lastMessageAt: serverTimestamp(),
      lastMessageSender: currentUser.uid
    });

    // Reset input & selected files after send
   // Reset input & selected files after send
setMessageInput('');
setSelectedFiles([]);

// ✅ Reset height back to 1 line after send
// ✅ Reset height back to 1 line after send
const textarea = document.querySelector('.dm-message-input-area textarea');
if (textarea) {
  textarea.style.height = 'auto'; // collapse
  // Force reflow to ensure immediate shrink on all devices
  void textarea.offsetHeight;
  textarea.style.height = Math.min(textarea.scrollHeight, 40) + 'px';
}
  } catch (err) {
    console.error('Failed to send message or upload files:', err);
  } finally {
    setIsSendingMessage(false);
  }
};




	useEffect(() => {
  const el = messagesListRef.current;

  const handleScroll = () => {
    if (!el) return;
    const threshold = 300; // px from bottom to still count as "at bottom"
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
const atBottom = distanceFromBottom <= threshold;

    setIsAtBottom(atBottom);
  };

  el?.addEventListener('scroll', handleScroll);

  return () => {
    el?.removeEventListener('scroll', handleScroll);
  };
}, []);




	const handleDeleteMessage = async (msgId) => {
  const confirmed = window.confirm('Are you sure you want to delete this message?');
  if (!confirmed) return;

  try {
    await deleteDoc(doc(db, 'conversations', chatId, 'messages', msgId));
  } catch (err) {
    console.error('Failed to delete message:', err);
  }
};


const actionButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#007bff',
};

const handleConfirmStartCall = () => {
  if (!canStartCall) return;
  setConfirmCallOpen(false);
  callActions.startCall(
    otherUser.id,
    {
      name: otherUser.firstInitialLastName || 'Unknown',
      rank: otherUser.rank || '',
      agency: otherUser.agency || 'Unknown Agency',
      avatarUrl: otherUser.avatarUrl || '',
    },
    currentUser
  );
};

const handleConfirmDeleteCallLog = async () => {
  if (!pendingDeleteLogId) return;
  try {
    await deleteDoc(
      doc(
        db,
        'conversations',
        chatId,
        'callLogs',
        pendingDeleteLogId
      )
    );
  } catch (err) {
    console.warn('Failed to delete call log:', err);
  } finally {
    setConfirmDeleteLogOpen(false);
    setPendingDeleteLogId(null);
  }
};

const handleViewProfile = () => {
  if (!otherUser?.id) return;
  setMenuOpen(false);
  navigate(`/profile/${otherUser.id}`);
};


const handleSaveEdit = async (msgId) => {
  const msg = messages.find((m) => m.id === msgId);
  if (!msg) return;

  const originalContent = msg.content || '';
  const editedContent = editContent;

  if (originalContent === editedContent) {
    // ❌ No changes made — skip Firestore update
    console.log('No content change. Skipping update.');
    setEditModeId(null); // Exit edit mode
    return;
  }

  try {
    const msgRef = doc(db, 'conversations', chatId, 'messages', msgId);
    await updateDoc(msgRef, {
      content: editedContent,
      editedAt: serverTimestamp(),
    });

    console.log('Message updated successfully.');
  } catch (err) {
    console.error('Failed to update message:', err);
  } finally {
    setEditModeId(null);
  }
};




	const resizeImage = (file, maxSize = 1024) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');

        const scale = maxSize / Math.max(img.width, img.height);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.8);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  });
};


 const fileInputRef = useRef(null);
 const cameraInputRef = useRef(null);



const MAX_FILE_SIZE_MB = 10;
const MAX_GIF_SIZE_MB = 15; // allow larger GIFs
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_GIF_SIZE = MAX_GIF_SIZE_MB * 1024 * 1024;


	const handleFileUpload = async (e) => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  const processedFiles = [];

  for (let file of files) {
    let uploadFile = file;

    const isGif = (file.type || '').toLowerCase() === 'image/gif' || /\.gif$/i.test(file.name || '');

    if (isGif) {
      if (file.size > MAX_GIF_SIZE) {
        alert(`${file.name} exceeds ${MAX_GIF_SIZE_MB}MB and was skipped.`);
        continue;
      }
      // do NOT resize GIFs to preserve animation
      processedFiles.push(uploadFile);
      continue;
    }

    if (file.size > MAX_FILE_SIZE) {
      if (file.type.startsWith('image/')) {
        const confirmResize = window.confirm(
          `${file.name} is over ${MAX_FILE_SIZE_MB}MB. Send reduced copy?`
        );
        if (confirmResize) {
          const resizedBlob = await resizeImage(file);
          uploadFile = new File([resizedBlob], file.name, { type: 'image/jpeg' });
        } else {
          alert(`${file.name} skipped.`);
          continue;
        }
      } else {
        alert(`${file.name} exceeds limit and was skipped.`);
        continue;
      }
    }

    processedFiles.push(uploadFile);
  }

  setSelectedFiles(prev => [...prev, ...processedFiles]);
  setAttachmentMenuOpen(false);
  e.target.value = '';
};


	const handleDeleteConversation = async () => {
  if (!window.confirm("Are you sure you want to delete this conversation? This cannot be undone.")) return;
  if (deleteInitiatedRef.current) return;

  deleteInitiatedRef.current = true;
  isClosingConversationRef.current = true;
  redirectHandledRef.current = true;
  setConversationDeleting(true);
  stopConversationSubscriptions();
  onBack();

  const fn = httpsCallable(functions, "deleteConversation");
  void fn({ chatId }).catch((error) => {
    console.error("Error deleting conversation:", error);
    alert("Failed to delete conversation.");
  });
};



	async function saveHandler() {
  try {
    const result = await saveToGallery(modalImage);
    alert(formatSaveMessage(result));
  } catch (err) {
    console.error('Save failed:', err);
    alert('Error: ' + (err.message || JSON.stringify(err)));
  }
}





async function getIceServers() {
  try {
    const fn = httpsCallable(functions, "getIceServers");
    const result = await fn();
    if (!hasLoggedIceServers) {
      console.log('[BlueWire] ICE servers from callable:', result.data);
      hasLoggedIceServers = true;
    }
    // support both token.iceServers and token.ice_servers
// normalize any Twilio field variations
const servers =
  result.data?.iceServers ??
  result.data?.ice_servers ??
  result.data?.token?.iceServers ??
  result.data?.token?.ice_servers ??
  [];

console.log("[BlueWire] Parsed ICE servers:", servers);

return servers;
  } catch (err) {
    console.error("Failed to get ICE servers:", err);
    return [];
  }
}

async function logCall(type, otherUserId, details = {}) {
  if (!currentUser?.uid || !otherUserId) return;

  try {
    await addDoc(collection(db, "users", currentUser.uid, "callLogs"), {
      type,
      otherUserId,
      timestamp: serverTimestamp(),
      details
    });
  } catch (err) {
    console.error("Failed to log call:", err);
  }
}

function logCallWithDuration(type, otherUserId) {
  if (!otherUserId) return;

  if (callStartTimeRef.current) {
    const durationMs = Date.now() - callStartTimeRef.current;
    logCall("duration", otherUserId, {
      seconds: Math.floor(durationMs / 1000)
    });
    callStartTimeRef.current = null;
  }

  logCall(type, otherUserId);
}

async function createPeerConnection(callId) {
  const iceServers = await getIceServers();

  const pc = new RTCPeerConnection({ iceServers });
  pcRef.current = pc;

  pc.onicecandidate = async (event) => {
    if (event.candidate) {
      const candidate = event.candidate.toJSON();
      const callRef = doc(db, "calls", callId);

      await updateDoc(callRef, {
        callerCandidates: arrayUnion(candidate)
      });
    }
  };

pc.ontrack = (event) => {
  console.log("Caller received remote track", event.track?.id);
  if (!remoteAudioRef.current) return;

  if (!remoteStreamRef.current) {
    remoteStreamRef.current = new MediaStream();
  } else {
    remoteStreamRef.current.getTracks().forEach((track) => {
      remoteStreamRef.current.removeTrack(track);
    });
  }

  remoteStreamRef.current.addTrack(event.track);
  remoteAudioRef.current.srcObject = remoteStreamRef.current;
  remoteAudioRef.current
    .play()
    .catch((err) => console.warn("Remote audio playback blocked:", err));
};

  return pc;
}

async function createPeerConnectionForAnswer(callId) {
  const iceServers = await getIceServers();
  const pc = new RTCPeerConnection({ iceServers });
  answerPcRef.current = pc;

  pc.onicecandidate = async (event) => {
    if (event.candidate) {
      const candidate = event.candidate.toJSON();
      await updateDoc(doc(db, "calls", callId), {
        calleeCandidates: arrayUnion(candidate)
      });
    }
  };

pc.ontrack = (event) => {
  console.log("Callee received remote track", event.track?.id);
  if (!remoteAudioRef.current) return;

  if (!remoteStreamRef.current) {
    remoteStreamRef.current = new MediaStream();
  } else {
    remoteStreamRef.current.getTracks().forEach((track) => {
      remoteStreamRef.current.removeTrack(track);
    });
  }

  remoteStreamRef.current.addTrack(event.track);
  remoteAudioRef.current.srcObject = remoteStreamRef.current;
  remoteAudioRef.current
    .play()
    .catch((err) => console.warn("Remote audio playback blocked:", err));
};
  return pc;
}

function listenForCallerCandidates(callId, pc) {
  const callRef = doc(db, "calls", callId);
  const processed = callerCandidatesProcessedRef.current;

  return onSnapshot(callRef, (snap) => {
    const data = snap.data();
    if (!data) return;

    if (data.status === "ended") {
      console.log("📴 Caller ended the call.");
      clearIncomingCallState();
      cleanupOutgoingCall();
      return;
    }

    if (!data.callerCandidates) return;

    data.callerCandidates.forEach(async (candidate) => {
      const key = JSON.stringify(candidate);
      if (processed.has(key)) return;
      processed.add(key);
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error("Error adding caller ICE:", err);
      }
    });
  }, (error) => {
    console.error("Caller candidates listener error:", error);
  });
}

async function getLocalAudioStream() {
  try {
    const baseConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1
    };

    const audioConstraints =
      typeof Capacitor?.getPlatform === "function" &&
      Capacitor.getPlatform() === "android"
        ? { ...baseConstraints, sampleRate: 48000 }
        : baseConstraints;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints
    });
    localStreamRef.current = stream;
    return stream;
  } catch (err) {
    console.error("Microphone permission denied:", err);
    throw err;
  }
}

function cleanupWebRTC() {
  if (iceUnsubRef.current) {
    iceUnsubRef.current();
    iceUnsubRef.current = null;
  }

  if (callerCandidatesUnsubRef.current) {
    callerCandidatesUnsubRef.current();
    callerCandidatesUnsubRef.current = null;
  }

  if (calleeCandidatesUnsubRef.current) {
    calleeCandidatesUnsubRef.current();
    calleeCandidatesUnsubRef.current = null;
  }

  if (pcRef.current) {
    pcRef.current.close();
    pcRef.current = null;
  }

  if (answerPcRef.current) {
    answerPcRef.current.close();
    answerPcRef.current = null;
  }

  if (localStreamRef.current) {
    localStreamRef.current.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
  }

  if (remoteAudioRef.current) {
    remoteAudioRef.current.srcObject = null;
  }

  if (remoteStreamRef.current) {
    remoteStreamRef.current.getTracks().forEach((track) => track.stop());
    remoteStreamRef.current = null;
  }

  calleeCandidatesProcessedRef.current.clear();
  callerCandidatesProcessedRef.current.clear();
  pendingCalleeCandidatesRef.current = [];
}

function cleanupCallUI() {
  setIsCalling(false);
  setOutgoingCallId(null);
  setOutgoingCallTarget(null);
  setInCall(false);
  setIsMuted(false);
  setIsSpeakerOn(false);
  if (Capacitor.isNativePlatform()) {
    try {
      resetSpeakerRoute();
    } catch (e) {
      console.warn("[AudioRouter] reset failed (ignored)", e);
    }
  }
  if (callTimeoutRef.current) {
    clearTimeout(callTimeoutRef.current);
    callTimeoutRef.current = null;
  }
  callPartnerIdRef.current = null;
  callStartTimeRef.current = null;
}

function cleanupOutgoingCall() {
  cleanupWebRTC();
  cleanupCallUI();
}

function clearIncomingCallState() {
  setIncomingCall(null);
  setIncomingCallId(null);
}

function flushPendingCalleeCandidates(pc) {
  if (!pc) return;
  const pending = pendingCalleeCandidatesRef.current.splice(0);
  pending.forEach(async (candidate) => {
    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.error("Error adding queued callee ICE:", err);
    }
  });
}

function queueOrAddCalleeCandidate(pc, candidate) {
  if (!pc?.currentRemoteDescription) {
    pendingCalleeCandidatesRef.current.push(candidate);
    return;
  }

  pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((err) => {
    console.error("Error adding callee ICE:", err);
  });
}

function listenForCalleeCandidates(callId, pc) {
  const callRef = doc(db, "calls", callId);
  const processed = calleeCandidatesProcessedRef.current;

  const unsub = onSnapshot(callRef, (snap) => {
    const data = snap.data();
    if (!data || !data.calleeCandidates) return;

    data.calleeCandidates.forEach((candidate) => {
      const key = JSON.stringify(candidate);
      if (processed.has(key)) return;
      processed.add(key);

      queueOrAddCalleeCandidate(pc, candidate);
    });
  }, (error) => {
    console.error("Callee candidates listener error:", error);
  });

  return unsub;
}

function toggleMute() {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "Legacy DirectChat toggleMute invoked — should be inert (engine owns mute)."
    );
  }
  return;

  if (!localStreamRef.current) return;

  const newMuted = !isMuted;
  localStreamRef.current.getAudioTracks().forEach((track) => {
    track.enabled = !newMuted;
  });
  setIsMuted(newMuted);
}

function toggleSpeaker() {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "Legacy DirectChat toggleSpeaker invoked — should be inert (engine owns speaker)."
    );
  }
  return;

  const newState = !isSpeakerOn;

  if (remoteAudioRef.current) {
    try {
      remoteAudioRef.current.setAttribute("playsinline", true);
      remoteAudioRef.current.setAttribute("webkit-playsinline", true);

      if (newState) {
        remoteAudioRef.current.setAttribute("x-webkit-airplay", "allow");
      } else {
        remoteAudioRef.current.removeAttribute("x-webkit-airplay");
      }
    } catch (err) {
      console.error("Speaker toggle failed:", err);
    }
  }

  setSpeakerRoute(newState);
  setIsSpeakerOn(newState);
}

async function endCall() {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "Legacy DirectChat endCall invoked — should be inert (engine owns endCall)."
    );
  }
  return;

  try {
    const callId = outgoingCallId || incomingCallId;
    const otherUserId =
      callPartnerIdRef.current ||
      outgoingCallTarget?.uid ||
      incomingCall?.from;

    logCallWithDuration("ended", otherUserId);
    cleanupOutgoingCall();
    clearIncomingCallState();

    if (callId) {
      await updateDoc(doc(db, "calls", callId), {
        status: "ended"
      });

      setTimeout(async () => {
        try {
          await deleteDoc(doc(db, "calls", callId));
        } catch (err) {
          console.error("Cleanup delete failed:", err);
        }
      }, 2000);
    }
  } catch (err) {
    console.error("End call error:", err);
  }
}

async function startCall(targetUserId) {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "Legacy DirectChat startCall invoked — should be inert (engine owns startCall)."
    );
  }
  return;

  try {
    setIsCalling(true);
    setOutgoingCallTarget({
      uid: targetUserId,
      name: otherUser?.firstInitialLastName || "Unknown",
      agency: otherUser?.agency || "Unknown Agency"
    });

    callPartnerIdRef.current = targetUserId;

    const callRef = await addDoc(collection(db, "calls"), {
      from: currentUser.uid,
      fromUserName: currentUser?.firstInitialLastName || currentUser?.displayName || "Unknown",
      fromAgency: currentUser?.agency || "Unknown Agency",
      to: targetUserId,
      status: "ringing",
      createdAt: serverTimestamp(),
      callerCandidates: [],
      calleeCandidates: []
    });

    setOutgoingCallId(callRef.id);
    console.log("?? Outgoing call created:", callRef.id);
    console.log("??? Initializing WebRTC...");

    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
    }
    callTimeoutRef.current = setTimeout(async () => {
      console.log("⏳ Call timeout — no answer");
      try {
        await updateDoc(doc(db, "calls", callRef.id), {
          status: "ended"
        });
        logCall("missed_outgoing", targetUserId);
      } catch (err) {
        console.error("Failed to mark call timeout:", err);
      }
      cleanupOutgoingCall();
      setTimeout(async () => {
        try {
          await deleteDoc(doc(db, "calls", callRef.id));
        } catch (err) {
          console.error("Cleanup delete failed:", err);
        }
      }, 2000);
    }, 30000);

    const pc = await createPeerConnection(callRef.id);

    console.log('[BlueWire] DirectChat startCall -> requesting mic permission');
    const hasPermission = await requestMicPermission();
    console.log('[BlueWire] DirectChat startCall -> mic permission result:', hasPermission);
    if (!hasPermission) {
      console.error("❌ Microphone permission denied — cannot start call.");
      try {
        await updateDoc(doc(db, "calls", callRef.id), {
          status: "ended"
        });
      } catch (permissionErr) {
        console.error("Failed to mark permission-denied call as ended:", permissionErr);
      }
      cleanupOutgoingCall();
      return;
    }

    console.log('[BlueWire] DirectChat startCall -> requesting local audio stream');
    const localStream = await getLocalAudioStream();
    console.log('[BlueWire] DirectChat startCall -> acquired local audio stream');

    localStream.getTracks().forEach((track) => {
      pc.addTrack(track, localStream);
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await updateDoc(doc(db, "calls", callRef.id), {
      offer
    });

    console.log("?? Offer sent to Firestore.");

    if (calleeCandidatesUnsubRef.current) {
      calleeCandidatesUnsubRef.current();
    }
    calleeCandidatesProcessedRef.current.clear();
    calleeCandidatesUnsubRef.current = listenForCalleeCandidates(callRef.id, pc);

    const unsub = onSnapshot(doc(db, "calls", callRef.id), (snap) => {
      const data = snap.data();
      if (!data) return;

      if (data.answer && !pc.currentRemoteDescription) {
        pc
          .setRemoteDescription(new RTCSessionDescription(data.answer))
          .then(() => {
            flushPendingCalleeCandidates(pc);
          })
          .catch((error) => {
            console.error("Failed to apply remote answer:", error);
          });
      }

      if (data.status === "declined") {
        console.log("❌ Callee declined call.");
        logCall("declined", callPartnerIdRef.current || targetUserId);
        cleanupOutgoingCall();
      }

      if (data.status === "ended") {
        console.log("📴 Remote user ended the call.");
        logCallWithDuration("ended", callPartnerIdRef.current || targetUserId);
        cleanupOutgoingCall();
      }
    }, (error) => {
      console.error("Direct call listener error:", error);
    });

    iceUnsubRef.current = unsub;
  } catch (err) {
    console.error("Error starting call:", err);
    cleanupOutgoingCall();
  }
}

async function cancelOutgoingCall() {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "Legacy DirectChat cancelOutgoingCall invoked — should be inert (engine owns cancelOutgoingCall)."
    );
  }
  return;

  if (!outgoingCallId) return;

  await endCall();
}

async function handleDeclineCall() {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "Legacy DirectChat handleDeclineCall invoked — should be inert (engine owns declineCall)."
    );
  }
  return;

  if (!incomingCallId) return;

  try {
    logCall("declined", incomingCall?.from);
    await updateDoc(doc(db, "calls", incomingCallId), {
      status: "declined"
    });
  } catch (err) {
    console.error("Error declining call:", err);
  } finally {
    clearIncomingCallState();
  }
}

async function handleAcceptCall() {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "Legacy DirectChat handleAcceptCall invoked — should be inert (engine owns acceptCall)."
    );
  }
  return;

  if (!incomingCallId || !incomingCall) return;

  try {
    console.log("[BlueWire] Accepting call:", incomingCallId);

    // Let caller know we've accepted so its UI can update
    await updateDoc(doc(db, "calls", incomingCallId), {
      status: "accepted"
    });

    const pc = await createPeerConnectionForAnswer(incomingCallId);

    if (!incomingCall.offer) {
      console.error("[BlueWire] No offer found for incoming call");
      return;
    }

    // 1) Apply caller's offer first so our answer SDP is correct
    await pc.setRemoteDescription(new RTCSessionDescription(incomingCall.offer));

    // 2) Then get our mic stream and attach it so it's included in the answer
    if (!(await requestMicPermission())) {
      console.error("[BlueWire] Microphone permission denied - cannot accept call.");
      return;
    }

    console.log("[BlueWire] handleAcceptCall -> requesting local audio stream");
    const localStream = await getLocalAudioStream();
    console.log("[BlueWire] handleAcceptCall -> acquired local audio stream");

    localStream.getTracks().forEach((track) => {
      pc.addTrack(track, localStream);
    });

    // 3) Now create and send the answer
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    await updateDoc(doc(db, "calls", incomingCallId), {
      answer
    });

    if (callerCandidatesUnsubRef.current) {
      callerCandidatesUnsubRef.current();
    }
    callerCandidatesProcessedRef.current.clear();
    callerCandidatesUnsubRef.current = listenForCallerCandidates(incomingCallId, pc);

    clearIncomingCallState();
    callPartnerIdRef.current = incomingCall.from;
    callStartTimeRef.current = Date.now();
    logCall("incoming", incomingCall.from);
    setInCall(true);
  } catch (err) {
    console.error("Error accepting call:", err);
  }
}




  return (
    <>
  
    {false && incomingCall && (
        <div className="incoming-call-overlay">
          <div className="incoming-call-container">
            <div className="incoming-call-title">Incoming Call</div>
            <div className="incoming-call-user">
              {incomingCall.fromUserName || "Unknown Caller"}
            </div>
            <div className="incoming-call-agency">
              {incomingCall.fromAgency ||
                incomingCall.agency ||
                "Unknown Agency"}
            </div>
            <div className="incoming-call-actions">
              <button className="decline-button" onClick={callActions.declineCall}>
                Decline
              </button>
              <button className="accept-button" onClick={callActions.acceptCall}>
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    <div className={`chat-window ${fromProfile ? 'first-open' : ''}`}>
      {false && inCall && (
        <div className="incall-overlay">
          <div className="incall-container">
            <div className="incall-title">In Call</div>
            <div className="incall-buttons">
              <button
                className={`incall-button ${isMuted ? 'active' : ''}`}
                onClick={callActions.toggleMute}
              >
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
              <button
                className={`incall-button ${isSpeakerOn ? 'active' : ''}`}
                onClick={callActions.toggleSpeaker}
              >
                Speaker
              </button>
              <button className="incall-button end" onClick={callActions.endCall}>
                End
              </button>
            </div>
          </div>
        </div>
      )}
      {false && isCalling && (
        <div className="calling-overlay">
          <div className="calling-container">
            <div className="calling-title">Calling…</div>

            <div className="calling-user">
            {outgoingCallTarget
  ? `${outgoingCallTarget.name} – ${outgoingCallTarget.agency}`
  : ""}
            </div>

            <button className="cancel-call-button" onClick={callActions.cancelOutgoingCall}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <div 
  className="chat-header" 
  style={{
  position: "fixed",
  top: "env(safe-area-inset-top, 0px)",
  left: 0,
  right: 0,
  zIndex: 1000
}}
>

       <div className="chat-title-with-menu">
  <button className="back-button" onClick={() => onBack()}>←</button>

  <div className="chat-title">
    {otherUser ? `${otherUser.firstInitialLastName || 'Unknown'} – ${otherUser.agency || 'Unknown Agency'}` : 'Loading...'}
  </div>

  {/* CALL BUTTON */}
  <div className="dm-call-confirm-wrap">
    <button 
      className="dm-call-button"
      disabled={!canStartCall}
      onClick={() => {
        if (!canStartCall) return;
        setConfirmCallOpen(true);
      }}
      style={{
      background: "#28a745",
      border: "none",
      borderRadius: "6px",
      color: "white",
      padding: "6px 8px",
      marginRight: "14px",
    cursor: "pointer"
  }}
>
  📞
    </button>
    {confirmCallOpen && (
      <div className="dm-confirm-popover">
        <div className="dm-confirm-title">Start a voice call?</div>
        <div className="dm-confirm-actions">
          <button
            className="dm-confirm-secondary"
            onClick={() => setConfirmCallOpen(false)}
          >
            Cancel
          </button>
          <button className="dm-confirm-primary" onClick={handleConfirmStartCall}>
            Call
          </button>
        </div>
      </div>
    )}
  </div>

	

  <div style={{ position: 'relative' }}>
    <button
      className="chat-menu-button"
      onClick={() => setMenuOpen(prev => !prev)}
    >⋯</button>


            {menuOpen && (
              <div className="chat-dropdown-menu" ref={menuRef}>

			{/* DISAPPEARING MESSAGES TIMER*/}
            {/*    <label>Message Timer</label>
                <select value={disappearTime} onChange={(e) => setDisappearTime(Number(e.target.value))}>
                  <option value={0}>Off</option>
                  <option value={5}>5 seconds</option>
                  <option value={60}>1 minute</option>
                  <option value={300}>5 minutes</option>
                  <option value={600}>10 minutes</option>
                  <option value={3600}>1 hour</option>
                  <option value={43200}>12 hours</option>
                </select>  */}
		  <hr style={{ margin: '8px 0' }} />
<button className="chat-menu-neutral-button" onClick={handleViewProfile}>
  View Profile
</button>
<button className="chat-menu-danger-button" onClick={handleDeleteConversation}>
  🗑️ Delete Conversation
</button>


              </div>
            )}
          </div>
        </div>
      </div>

   <div className="messages-list" ref={messagesListRef}>
  {(() => {
    const timeline = [];

    messages.forEach((msg) => {
      const ts =
        msg.createdAt?.toDate?.() ||
        (msg.createdAt instanceof Date ? msg.createdAt : null);
      timeline.push({
        type: 'message',
        id: msg.id,
        createdAt: ts,
        message: msg,
      });
    });

    callLogs.forEach((log) => {
      const ts =
        log.startedAt?.toDate?.() ||
        log.createdAt?.toDate?.() ||
        (log.createdAt instanceof Date ? log.createdAt : null);
      timeline.push({
        type: 'callLog',
        id: log.callId || log.id,
        createdAt: ts,
        log,
      });
    });

    timeline.sort((a, b) => {
      const at = a.createdAt?.getTime?.() || 0;
      const bt = b.createdAt?.getTime?.() || 0;
      return at - bt;
    });

    return timeline.map((entry, index) => {
      const thisDate = entry.createdAt;
      const prevDate =
        index > 0 ? timeline[index - 1].createdAt : null;

      const isNewDay =
        !!thisDate &&
        (!prevDate ||
          thisDate.toDateString() !== prevDate?.toDateString());

      const timeLabel =
        thisDate &&
        thisDate.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });

      if (entry.type === 'callLog') {
        const log = entry.log;
        const isMissed = log.status === 'missed';
        const isDeclined = log.status === 'declined';
        const isCompleted = log.status === 'completed';

        const isCaller = log.callerId === currentUser.uid;
        const arrow = isCaller ? '↗' : '↙';

        let text;
        if (isMissed) {
          if (isCaller) {
            text = `Unanswered Call ${timeLabel || ''}`;
          } else {
            text = `Missed Call ${timeLabel || ''}`;
          }
        } else if (isDeclined) {
          text = `Declined Call ${timeLabel || ''}`;
        } else if (isCompleted) {
          const seconds = log.durationSeconds || 0;
          const minutes = Math.floor(seconds / 60);
          const remaining = seconds % 60;
          const durationLabel = `(${minutes}m ${remaining}s)`;
          text = `${arrow} ${timeLabel || ''} ${durationLabel}`;
        } else {
          text = `${arrow} ${timeLabel || ''}`;
        }

        return (
          <React.Fragment key={`log-${entry.id}`}>
            {isNewDay && (
              <div
                style={{
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  color: '#999',
                  margin: '12px 0',
                }}
              >
                {thisDate?.toLocaleDateString()}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '8px 0',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  maxWidth: '80%',
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  borderRadius: '999px',
                  padding: '6px 10px',
                  fontSize: '0.85rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow:
                    '0 1px 2px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.06)',
                }}
              >
                <span>{text}</span>
                <button
                  onClick={async () => {
                    setPendingDeleteLogId(log.callId || entry.id);
                    setConfirmDeleteLogOpen(true);
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    padding: 0,
                  }}
                >
                  X
                </button>
                {confirmDeleteLogOpen && pendingDeleteLogId === (log.callId || entry.id) && (
                  <div className="dm-confirm-popover dm-confirm-popover-inline">
                    <div className="dm-confirm-title">Delete log?</div>
                    <div className="dm-confirm-actions">
                      <button
                        className="dm-confirm-secondary"
                        onClick={() => setConfirmDeleteLogOpen(false)}
                      >
                        Cancel
                      </button>
                      <button className="dm-confirm-primary" onClick={handleConfirmDeleteCallLog}>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      }

      const msg = entry.message;

      return (
        <React.Fragment key={msg.id}>
          {isNewDay && (
            <div
              style={{
                textAlign: 'center',
                fontSize: '0.8rem',
                color: '#999',
                margin: '12px 0',
              }}
            >
              {thisDate?.toLocaleDateString()}
            </div>
          )}

          <div
  className={`message-bubble ${
    msg.senderId === currentUser.uid ? 'sent' : 'received'
  } ${editModeId === msg.id ? "editing" : ""}`}
>
          <div className="message-meta">
  {msg.senderName || 'Unknown'}
  {msg.senderId === currentUser.uid && currentUser.agency
    ? `, ${currentUser.agency}`
    : otherUser?.agency
    ? `, ${otherUser.agency}`
    : ''}
</div>


         {editModeId === msg.id ? (
  <textarea
    ref={editRef}
    value={editContent}
    placeholder="Add or edit caption..."   // ✅ add this line
    onChange={(e) => setEditContent(e.target.value)}
    className="edit-textarea"
  />
) : (
  <div className="message-content">{msg.content}</div>
)}


    {msg.mediaAttachments && msg.mediaAttachments.length > 0 && (
  <div className="attachments" style={{ marginTop: '8px' }}>
    {msg.mediaAttachments.map((att, idx) => {
  const isEditing = editModeId === msg.id;

  if (att.type === 'image') {
    return (
      <div
        key={idx}
        style={{
          marginTop: '6px',
          position: "relative",
          display: "inline-block",
        }}
      >
        <img
          src={att.url}
          alt="uploaded"
          style={{
            width: isEditing ? "60px" : "150px",
            height: isEditing ? "60px" : "150px",
            objectFit: "cover",
            borderRadius: "6px",
            cursor: isEditing ? "default" : "pointer",
            opacity: isEditing ? 0.9 : 1,
          }}
          onClick={() => !isEditing && setModalImage(att.url)}
        />

        {/* DELETE BUTTON VISIBLE ONLY IN EDIT MODE */}
        {isEditing && (
          <button
  data-ignore-outside="true"
  onMouseDown={(e) => {
    e.stopPropagation();
    e.preventDefault();
  }}
  onClick={async () => {
    const newMedia = msg.mediaAttachments.filter((_, i) => i !== idx);
    await updateDoc(
      doc(db, "conversations", chatId, "messages", msg.id),
      { mediaAttachments: newMedia }
    );
  }}
  style={{
    position: "absolute",
    top: "-6px",
    right: "-6px",
    background: "rgba(255,0,0,0.9)",
    border: "none",
    color: "white",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    fontSize: "13px",
    fontWeight: "bold",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 4px rgba(0,0,0,0.5)",
  }}
>
  ✕
</button>

        )}
      </div>
    );
  } else {
    return (
      <div key={idx} style={{ marginTop: "8px" }}>
        <a
          href={att.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: "0.8rem",
            color: "#007bff",
            textDecoration: "underline",
          }}
        >
          📎 Open Attachment
        </a>
      </div>
    );
  }
})}
  </div>
)}


          {msg.expiresIn && msg.createdAt && (
            <div className="countdown-bar">
              <div
                style={{
                  width: `${Math.max(
                    0,
                    100 -
                      ((Date.now() - msg.createdAt.toMillis()) /
                        (msg.expiresIn * 1000)) *
                        100
                  )}%`,
                  height: '3px',
                  backgroundColor: 'red',
                  marginTop: '6px',
                  transition: 'width 1s linear',
                }}
              />
            </div>
          )}

          {msg.senderId === currentUser.uid && (
            <div
              className="read-receipt"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <div className="message-actions">
                {editModeId === msg.id ? (
                  <>
                    <button
                      onClick={() => handleSaveEdit(msg.id)}
                      style={actionButtonStyle}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => setEditModeId(null)}
                      style={actionButtonStyle}
                    >
                      ❌
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditModeId(msg.id);
                        setEditContent(msg.content || '');
                      }}
                      style={actionButtonStyle}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteMessage(msg.id)}
                      style={actionButtonStyle}
                    >
                      ❌
                    </button>
                    <span className="read-receipt">
  								{msg.readBy?.includes(otherUser?.id) ? <span className="read-icon">🕶️</span> : '⇧'}
			</span>

                  </>
                )}
              </div>
            </div>
          )}

         <div
  style={{
    fontSize: '0.75rem',
    color: '#00ffcc',
    marginTop: '2px',
    textAlign: msg.senderId === currentUser.uid ? 'right' : 'left',
  }}
>
  {msg.editedAt
    ? `Edited: ${
        msg.editedAt.toDate
          ? msg.editedAt.toDate().toLocaleString([], {
              month: 'numeric',
              day: 'numeric',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : new Date(msg.editedAt).toLocaleString([], {
              month: 'numeric',
              day: 'numeric',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
      }`
    : msg.createdAt?.toDate().toLocaleString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}
	   </div>
        </div>
      </React.Fragment>
      );
    });
  })()}

</div>

{modalImage && (
  <div className="dm-image-modal">
    <ZoomableImage src={modalImage} alt="Full Size" />
    <div className="dm-image-modal-buttons">
      <button onClick={saveHandler}>⬇️ Save Image</button>
      <button onClick={() => setModalImage(null)}>← Back</button>
    </div>
  </div>
)}

	{selectedFiles.length > 0 && (
  <div className="dm-file-preview-list">
    {selectedFiles.map((file, index) => (
      <div
        key={index}
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '4px',
          gap: '8px',
        }}
      >
        {file.type.startsWith('image/') ? (
          <img
            src={URL.createObjectURL(file)}
            alt="preview"
            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
          />
        ) : (
          <span>📄 {file.name}</span>
        )}
        <button
  onClick={() => handleRemoveFile(index)}
  style={{
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
  }}
>
  ❌
</button>

      </div>
    ))}
  </div>
)}

      <div className="dm-message-input-area">
  <div className="dm-attachment-picker" ref={attachmentPickerRef}>
  <input
    type="file"
    multiple
    style={{ display: 'none' }}
    ref={fileInputRef}
    onChange={handleFileUpload}
  />
  <input
    type="file"
    accept="image/*"
    capture="environment"
    style={{ display: 'none' }}
    ref={cameraInputRef}
    onChange={handleFileUpload}
  />
  <button
    type="button"
    className="dm-upload-button"
    aria-label="Add attachment"
    aria-expanded={attachmentMenuOpen}
    onClick={() => setAttachmentMenuOpen(prev => !prev)}
  >
    +
  </button>
  {attachmentMenuOpen && (
    <div className="dm-attachment-menu">
      <button
        type="button"
        className="dm-attachment-menu-item"
        onClick={() => {
          fileInputRef.current?.click();
          setAttachmentMenuOpen(false);
        }}
      >
        Gallery
      </button>
      <button
        type="button"
        className="dm-attachment-menu-item"
        onClick={() => {
          cameraInputRef.current?.click();
          setAttachmentMenuOpen(false);
        }}
      >
        Camera
      </button>
    </div>
  )}
  </div>
<textarea
  placeholder="Type a message..."
  value={messageInput}
  disabled={!conversationExists || conversationDeleting}
  onChange={(e) => {
    setMessageInput(e.target.value);

    // Auto-resize logic
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 106) + "px"; // cap at 4 lines
  }}
  rows={1}
/>

  <button
    onClick={handleSendMessage}
    className="dm-send-button"
    disabled={!conversationExists || conversationDeleting || isSendingMessage}
  >
  {isSendingMessage ? 'Sending...' : 'Send'}
</button>
</div>
	
    </div>
    </>
  );
};

export default DirectChat;
